/**
 * Which workspace each sign-in attempt began in. An attempt belongs to that workspace's regional
 * API, so the callback must complete it there; if the person switched workspace while signing in,
 * the callback has to know where to go back to rather than ask the wrong workspace.
 *
 * `localStorage`, not `sessionStorage`: the sign-in window is a separate, same-origin tab and has
 * to read what the starting page wrote. Storage can be missing or refuse writes (private modes,
 * blocked site data); then nothing is remembered and the callback behaves as it always has.
 */
const KEY = "abacor:connection-attempt-workspaces";
/** Attempts last minutes; a day is generous and keeps the record from growing. */
const KEEP_MS = 24 * 60 * 60 * 1000;

type Remembered = Record<string, { workspaceId: string; at: number }>;

function read(): Remembered {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(KEY) ?? "{}");
    return typeof value === "object" && value !== null ? (value as Remembered) : {};
  } catch {
    return {};
  }
}

function write(entries: Remembered) {
  try {
    if (Object.keys(entries).length === 0) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(entries));
  } catch {
    // Nothing remembered; the callback completes in whichever workspace is active.
  }
}

export function rememberAttemptWorkspace(attemptId: string, workspaceId: string, now = Date.now()) {
  const kept = Object.fromEntries(
    Object.entries(read()).filter(([, entry]) => now - entry.at < KEEP_MS),
  );
  write({ ...kept, [attemptId]: { workspaceId, at: now } });
}

export function rememberedAttemptWorkspace(attemptId: string): string | null {
  const workspaceId = read()[attemptId]?.workspaceId;
  return typeof workspaceId === "string" && workspaceId ? workspaceId : null;
}

export function forgetAttemptWorkspace(attemptId: string) {
  const entries = read();
  if (!(attemptId in entries)) return;
  delete entries[attemptId];
  write(entries);
}
